'use client'

// The post chain. One pass, because there can only be one.
//
// This started as BloomPass and became this the moment a second effect
// was wanted: a prioritised useFrame subscriber IS the renderer, so two
// of them do not compose, they fight, and the second one to mount wins
// the frame while the first quietly does nothing. Everything that wants
// to touch the final image has to go in this graph.
//
// The rule this still ships under: post-processing has been off the table
// in this project because a broken frame rate costs more than any look is
// worth. Nothing here is on by default. The graph is built from whatever
// is actually asked for, and when nothing is, this component does not
// mount at all and r3f renders the world exactly as it always did.
//
// WHAT CAN BE IN IT
//
//   bloom       ?bloom=1. Selective, not thresholded: the scene renders
//               once with MRT writing the emissive channel alongside
//               colour, and only THAT feeds the bloom, so windows and the
//               stone pay and the bright sky is free. Half-res chain at
//               dpr 0.8, so the blur touches ~13% of screen pixels before
//               the mips shrink it further.
//
//   afterimage  While he is tripping. Each frame is blended into the last
//               instead of replacing it, so movement smears and still
//               things stay sharp, which is what the eye actually does on
//               mushrooms. It costs one full-screen feedback buffer and
//               ONLY exists while the trip level is up: the graph is
//               rebuilt when the recipe changes, so a sober world pays
//               nothing for it.
//
// The per-material dither is unaffected either way: it lives inside the
// materials, so it is baked into the scene pass and rides through.
//
// Live tuning in the console while it is mounted:
//   setBloom(strength, radius, threshold)   e.g. setBloom(0.35, 0.4, 0)
//   setTrip(level)                          0 to 1, for looking at it
//                                           without eating anything

import { useEffect, useRef, useState } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { useProgress } from '@react-three/drei'
import * as THREE from 'three/webgpu'
import { pass, mrt, output, emissive, uniform, saturation } from 'three/tsl'
import { bloom } from 'three/examples/jsm/tsl/display/BloomNode.js'
import { afterImage } from 'three/examples/jsm/tsl/display/AfterImageNode.js'
import useGameStore from '../../../stores/gameStore'

// Understated on purpose. Bloom that announces itself is the exact kawaii
// killer Sam avoids; this is meant to read as air around a bulb, not as a
// music video. Tune live before arguing with these.
const STRENGTH = 0.35
const RADIUS = 0.4
const THRESHOLD = 0

// How much of the previous frame survives into this one. Higher is a
// longer smear. Sober is not in this range at all: the pass is not in the
// graph then.
//
// The floor is deliberately well short of AfterImageNode's own 0.96
// default, which holds an image long enough to read as a broken monitor
// rather than as a drug. The ceiling is where a turning camera starts
// leaving readable ghosts of the town behind it.
const DAMP_MIN = 0.62
const DAMP_MAX = 0.90

// How fast the smear follows the trip envelope. Slow: coming up should be
// something you notice happening rather than a switch being thrown, and
// the sim's own envelope already ramps over minutes.
const EASE = 0.02

// Below this the pass leaves the graph entirely.
const TRIP_FLOOR = 0.01

// How much the world's colours come up while he is up. Multiplies the
// composite BEFORE the afterimage, so the ghosts inherit the saturation
// rather than re-saturating on every bounce. One multiply per pixel,
// which is the cheapest possible way to make the trip read as a
// different world; the per-material dither is never touched.
const SAT_MAX = 1.35

function PostInner() {
  const { gl, scene, camera } = useThree()
  const post = useRef(null)
  const recipe = useRef('')
  const dead = useRef(false)
  const damp = useRef(null)
  const sat = useRef(null)
  const level = useRef(0)
  // Set by setTrip() in the console. null means "ask the world", so the
  // override can be handed back with setTrip(null) rather than being a
  // one-way door for the session.
  const manual = useRef(initialTrip())

  useEffect(() => {
    window.setTrip = (v) => {
      manual.current = v == null ? null : Math.max(0, Math.min(1, Number(v) || 0))
      return manual.current == null
        ? 'trip level handed back to the world'
        : `trip level held at ${manual.current}`
    }
    return () => {
      delete window.setBloom
      delete window.setTrip
      post.current?.dispose?.()
      post.current = null
    }
  }, [])

  // Priority 1 makes this the renderer: r3f sees a prioritised subscriber
  // and stops auto-rendering, and hands the job back the moment this
  // unmounts. The pass renders the same scene, camera, shadows and
  // materials as the default path, plus the emissive target for the glow.
  useFrame(() => {
    if (dead.current) {
      gl.render(scene, camera)
      return
    }

    // What the world says, or what the console is holding it at.
    const target = manual.current != null
      ? manual.current
      : (useGameStore.getState().environment?.tripLevel || 0)
    level.current += (target - level.current) * EASE
    if (level.current < 0.0005) level.current = 0

    const wantTrip = level.current > TRIP_FLOOR
    const want = `${bloomRequested() ? 'b' : ''}${wantTrip ? 't' : ''}`

    try {
      if (want !== recipe.current) {
        // Rebuild. Only ever on a real change of recipe, which in a whole
        // session is: bloom decided once at load, and the trip coming up
        // and going down again.
        post.current?.dispose?.()
        post.current = null
        damp.current = null
        sat.current = null
        recipe.current = want

        if (want) {
          const scenePass = pass(scene, camera)
          // MRT only when bloom needs the emissive channel. The first
          // version attached it unconditionally, which made a trip-only
          // frame pay for a second full-resolution render target it never
          // read. A trip with bloom off is now one colour target plus the
          // afterimage's own feedback buffer, and nothing else.
          if (want.includes('b')) scenePass.setMRT(mrt({ output, emissive }))
          let out = want.includes('b') ? scenePass.getTextureNode('output') : scenePass
          if (want.includes('b')) {
            const glow = bloom(scenePass.getTextureNode('emissive'), STRENGTH, RADIUS, THRESHOLD)
            out = out.add(glow)
            window.setBloom = (s, r, t) => {
              if (s != null) glow.strength.value = s
              if (r != null) glow.radius.value = r
              if (t != null) glow.threshold.value = t
              return `bloom strength ${glow.strength.value} radius ${glow.radius.value} threshold ${glow.threshold.value}`
            }
          }

          // Last in the chain on purpose: the smear should carry the
          // finished image, glow and saturation included, rather than the
          // effects re-applying to their own ghosts.
          if (want.includes('t')) {
            sat.current = uniform(1)
            out = saturation(out, sat.current)
            damp.current = uniform(DAMP_MIN)
            out = afterImage(out, damp.current)
          }

          const p = new THREE.PostProcessing(gl)
          p.outputNode = out
          post.current = p
          console.log(`[postfx] built: ${want.includes('b') ? 'bloom ' : ''}${want.includes('t') ? 'afterimage' : ''}`.trim())
        } else {
          console.log('[postfx] nothing wanted, back to the plain renderer')
        }
      }

      if (!post.current) {
        gl.render(scene, camera)
        return
      }
      if (damp.current) {
        damp.current.value = DAMP_MIN + (DAMP_MAX - DAMP_MIN) * Math.min(1, level.current)
      }
      if (sat.current) {
        sat.current.value = 1 + (SAT_MAX - 1) * Math.min(1, level.current)
      }
      post.current.render()
    } catch (err) {
      // ONE bad frame demotes the whole thing for the session. A trial
      // effect is never allowed to take the world down with it: the first
      // version of the bloom pass built its graph while models were still
      // streaming, threw per-frame, and the error boundary's remount churn
      // set off r3f's suspense Block cascade.
      dead.current = true
      console.error('[postfx] threw, demoted to the plain renderer for this session:', err?.message || err)
      gl.render(scene, camera)
    }
  }, 1)

  return null
}

export function PostFX() {
  // Wait out the whole load. Mounting the prioritised subscriber while
  // models are still streaming is what set off the suspense cascade, and
  // an effect has no business existing before there is a world to affect.
  //
  // NOT the useProgress() hook: a loader can start synchronously inside a
  // SIBLING'S render (a Shard suspending on its glb), the global manager
  // writes its store, and a hook subscription here re-renders PostFX in
  // the middle of that sibling's render pass, which is the #185 crash
  // class wearing a new hat. So the progress store is read imperatively
  // in an effect and latched: once the first load settles, settled stays
  // true, because a later prop streaming in must not unmount the pass
  // mid-trip. `total` separates "still streaming" from "nothing was ever
  // queued" (a disconnected local viewer never loads an asset).
  const [settled, setSettled] = useState(() => {
    const s = useProgress.getState()
    return !s.active && (s.total === 0 || s.progress >= 100)
  })
  useEffect(() => {
    if (settled) return undefined
    return useProgress.subscribe((s) => {
      if (!s.active && (s.total === 0 || s.progress >= 100)) {
        queueMicrotask(() => setSettled(true))
      }
    })
  }, [settled])

  // Nothing mounts for a sober world with no flag: r3f keeps the render
  // loop it has always had. Once something has asked for the chain it
  // stays mounted, because unmounting on the way down a trip would hand
  // the loop back mid-fade and pop.
  const [armed, setArmed] = useState(() => bloomRequested() || initialTrip() != null)
  useEffect(() => {
    if (armed) return undefined
    return useGameStore.subscribe((s) => {
      if ((s.environment?.tripLevel || 0) > TRIP_FLOOR) {
        // Deferred for the same reason as the settle latch above.
        queueMicrotask(() => setArmed(true))
      }
    })
  }, [armed])

  if (!settled || !armed) return null
  return <PostInner />
}

/**
 * The bloom gate. Read on demand rather than cached: the recipe check
 * runs per frame and a URL flag is cheap to parse, and keeping it a
 * function means the pass has one source of truth for it.
 */
export function bloomRequested() {
  if (typeof location === 'undefined') return false
  return new URLSearchParams(location.search).get('bloom') === '1'
}

/**
 * ?trip=0.8 for looking at the smear without the sim running. Null when
 * absent, which is what hands the level back to the world.
 */
export function initialTrip() {
  if (typeof location === 'undefined') return null
  const raw = new URLSearchParams(location.search).get('trip')
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 1
}

/** Whether the chain needs to exist at all this session. */
export function postFxRequested() {
  return bloomRequested() || initialTrip() != null
}

export default PostFX
